import {HttpErrorResponse} from '@angular/common/http';
import {computed, inject, Injectable, Provider, signal, Type} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {finalize, map} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {z} from 'zod';
import {TranslateService} from '@brunata-angular/util';
import {errorBody} from '../../../shared/utils/errorInterceptor';
import {SavedExportsService} from '../../saved-exports/saved-exports.service';
import {injectHoldsAdvancedDelivery} from './advanced-delivery';
import {
    EXPORT_FORM,
    EXPORT_TEST_ACTION,
    EXPORT_TEST_SOURCE,
    ExportTestAction,
    ExportTestSource,
} from '../models/export-form.model';

// The backend keeps its send window only after an SFTP upload was attempted or its throttle refused
// the call, and answers both with WS_PRECONDITIONS_FAILED; every other failure hands the window back.
const sendWindowHeldErrorSchema = z.object({errorCode: z.literal('WB_WEBSERVICES_0004')});

function backendHoldsSendWindow(error: unknown): boolean {
    return (
        error instanceof HttpErrorResponse &&
        sendWindowHeldErrorSchema.safeParse(errorBody(error.error)).success
    );
}

@Injectable()
export class ExportTestService implements ExportTestAction {
    private readonly savedExportsService = inject(SavedExportsService);
    private readonly toastrService = inject(ToastrService);
    private readonly translateService = inject(TranslateService);
    private readonly source = inject(EXPORT_TEST_SOURCE);
    private readonly exportForm = inject(EXPORT_FORM);
    private readonly schedule = this.exportForm.controls.schedule;
    private readonly recipients = this.schedule.controls.recipients;
    private readonly name = this.exportForm.controls.name;

    private readonly channel = toSignal(this.schedule.controls.deliveryChannel.valueChanges, {
        initialValue: this.schedule.controls.deliveryChannel.value,
    });

    private readonly sftpConnectionId = toSignal(
        this.schedule.controls.sftpConnectionId.valueChanges,
        {initialValue: this.schedule.controls.sftpConnectionId.value},
    );

    private readonly holdsAdvancedDelivery = injectHoldsAdvancedDelivery();

    readonly running = signal(false);
    readonly cooldownSeconds = this.savedExportsService.testCooldownSeconds;

    // The email-list CVA drives this control invalid and pushes a null value on an empty list or a
    // malformed address, so validity is the "can't send a test yet" signal that never reads the null.
    private readonly areRecipientsValid = toSignal(
        this.recipients.statusChanges.pipe(map(status => status === 'VALID')),
        {initialValue: this.recipients.valid},
    );

    // The attachment is named after the export, so the test needs a name the backend will accept:
    // validity covers both the blank name and one longer than the column it is stored in.
    private readonly isNameValid = toSignal(
        this.name.statusChanges.pipe(map(status => status === 'VALID')),
        {initialValue: this.name.valid},
    );

    readonly disabled = computed(
        () =>
            this.running() ||
            this.cooldownSeconds() > 0 ||
            !this.isNameValid() ||
            (this.channel() === 'SFTP'
                ? this.sftpConnectionId() === null || this.holdsAdvancedDelivery() !== true
                : !this.areRecipientsValid()),
    );

    run(): void {
        if (this.disabled()) {
            return;
        }
        const request = this.source.buildTestRequest();
        this.running.set(true);

        this.savedExportsService
            .sendTestDelivery(request)
            .pipe(finalize(() => this.running.set(false)))
            .subscribe({
                next: () => {
                    this.savedExportsService.startTestCooldown();
                    this.toastrService.success(
                        this.translateService.translate(
                            'savedexport_test_success',
                            'online',
                            'export',
                        ),
                    );
                },
                error: (error: unknown) => {
                    if (backendHoldsSendWindow(error)) {
                        this.savedExportsService.startTestCooldown();
                    }
                },
            });
    }
}

// Opts a flow's schedule step into the "Run test" button; the flow passes its own form service so
// the test delivery uses the format that flow has selected.
export function exportTestProviders(formService: Type<ExportTestSource>): Provider[] {
    return [
        ExportTestService,
        {provide: EXPORT_TEST_ACTION, useExisting: ExportTestService},
        {provide: EXPORT_TEST_SOURCE, useExisting: formService},
    ];
}
